import { ReactComponent as ArrowIcon } from "assets/icons/down-arrow.svg";
import { spacing } from "globalStyles";
import useToggleOpen from "hooks/useToggleOpen";
import React, { ReactNode, useCallback, useEffect } from "react";
import styled from "styled-components";
import Heading from "ui/Text/Heading";
import { AccordionColor } from "./index";

const ANIMATION_DURATION = 0.5;

export interface AccordionItemProps {
  label: string;
  content: ReactNode;
  approximateMaxHeight: number;
  hasVerticalScroll?: boolean;
  id?: string;
}

interface Props extends AccordionItemProps {
  color: AccordionColor;
}

const AccordionItem: React.FC<Props> = ({
  label,
  content,
  approximateMaxHeight,
  color,
  id,
  hasVerticalScroll = false,
}) => {
  const { isOpen, toggleOpen } = useToggleOpen();

  const handleUrlChange = useCallback(() => {
    const urlId = window.location.href.split("#")[1];

    if (!id || urlId !== id) {
      return;
    }

    if (!isOpen) {
      toggleOpen();
    }

    window.history.pushState({}, "", window.location.href.replace(`#${id}`, ""));
  }, [isOpen, toggleOpen, id]);

  useEffect(() => {
    if (!id) {
      return;
    }

    window.addEventListener("hashchange", handleUrlChange);

    return () => window.removeEventListener("hashchange", handleUrlChange);
  }, [id, handleUrlChange]);

  return (
    <Container id={id}>
      <InnerContainer>
        <HeaderContainer color={color} onClick={toggleOpen}>
          <Heading variant="h5" color="secondary">
            {label}
          </Heading>
          <Arrow $opened={isOpen} />
        </HeaderContainer>
        <ExtraInfoContainer
          hiddenInfoApproximateMaxHeight={approximateMaxHeight}
          $hasVerticalScroll={hasVerticalScroll}
          $opened={isOpen}
          color={color}
        >
          {content}
        </ExtraInfoContainer>
      </InnerContainer>
    </Container>
  );
};

export default AccordionItem;

const Container = styled.div`
  display: flex;
  width: 100%;
`;

const InnerContainer = styled.div`
  width: 100%;
`;

const HeaderContainer = styled.div<{ color: AccordionColor }>`
  display: flex;
  cursor: pointer;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.medium}px;
  width: calc(100% - ${2 * spacing.small});
  border-bottom: ${({ theme, color }) =>
    `1px solid ${theme.colors.accordion[color].border}`};
`;

const Arrow = styled(ArrowIcon)<{ $opened: boolean }>`
  transform: ${({ $opened }) => ($opened ? `rotate(180deg)` : `rotate(0deg)`)};
  transition: transform ${ANIMATION_DURATION}s ease;
  stroke: ${({ theme }) => theme.colors.accordion.primary.arrowColor};
  min-width: 16px;
`;

const ExtraInfoContainer = styled.div<{
  $opened: boolean;
  hiddenInfoApproximateMaxHeight: number;
  $hasVerticalScroll: boolean;
  color: AccordionColor;
}>`
  display: flex;
  overflow: ${({ $opened, $hasVerticalScroll }) =>
    $opened && $hasVerticalScroll ? `auto` : `hidden`};
  transition: max-height ${ANIMATION_DURATION}s ease;
  background-color: ${({ theme, color }) =>
    theme.colors.accordion[color].hiddenInfo.background};
  max-height: ${({ $opened, hiddenInfoApproximateMaxHeight }) =>
    $opened ? `${hiddenInfoApproximateMaxHeight}px` : 0};
`;
